import { EPSILON } from '../../../src/measurement/MeasureCommon';

const av = Autodesk.Viewing;
const MeasureCommon = av.MeasureCommon;

const SNAP_PRECISION = 0.001;
const CURVED_SURFACE_SNAP_TOLERANCE = 0.1;
const POINT_PRECISION = 0.001;
const MAX_POLYGON_AXES = 2;

/**
 * Helper function to check if a point is in a line segment
 * @param {THREE.Vector3} point - The point to check
 * @param {THREE.Vector3}  vA - The first vertex of the line segment
 * @param {THREE.Vector3}  vB - The second vertex of the line segment
 * @param {Boolean} isFlat - Whether the line segment lies on a flat or curved surface edge
 * @returns {Boolean} true if the point is in the line segment. For now we assume that the line segment is flat.
 */
function isInLineSegment(point, vA, vB, isFlat = true) {
  const tolerance = isFlat ? SNAP_PRECISION : CURVED_SURFACE_SNAP_TOLERANCE;
  const ptOnEdge = getPerpPointToEdge(point, vA, vB);
  if (!ptOnEdge) {
    return false;
  }
  const distanceToSegment = ptOnEdge.distanceTo(point);
  return distanceToSegment <= tolerance;
}

/**
 * Computes the closest point on the line segment (vA, vB) to refPoint by orthogonal projection.
 * @param {THREE.Vector3} refPoint - The reference point from which to find the perpendicular
 * @param {THREE.Vector3} vA - The first vertex of the edge
 * @param {THREE.Vector3} vB - The second vertex of the edge
 * @returns {THREE.Vector3} The closest point on the line segment to refPoint
 */
function getPerpPointToEdge(refPoint, vA, vB) {
  const lineLength = vA.distanceTo(vB);
  const refVec = new THREE.Vector3().subVectors(refPoint, vA);
  const lineDirection = new THREE.Vector3().subVectors(vB, vA).normalize();

  const distance = refVec.dot(lineDirection);

  if (distance < 0 || distance > lineLength) {
    return null;
  }

  return new THREE.Vector3().addVectors(vA, lineDirection.multiplyScalar(distance));
}

/**
 * Calculates the dimension points for an edge based on intersection with a hit point
 * @param {Object} edgeData - Object containing edge geometry and intersection data
 * @param {Object} edgeData.geomLine - The edge geometry containing vertices
 * @param {Array<THREE.Vector3>} edgeData.geomLine.vertices - Array of vertex points defining the edge
 * @param {THREE.Vector3} edgeData.faceNormal - Normal vector of the face containing the edge
 * @param {THREE.Vector3} edgeData.intersectPoint - Point where ray intersects the surface
 * @returns {Array|null} Array of dimension points if valid edge data exists, null otherwise
 */
function getDimensionPoints(edgeData) {
  let dimensionPoints = null;
  const axes = [];
  const lineVertices = edgeData.geomLine?.vertices;
  const faceNormal = edgeData.faceNormal;
  const hitPoint = edgeData.intersectPoint;

  if (!lineVertices || lineVertices.length < 2 || !faceNormal || !hitPoint) {
    return dimensionPoints; // No edges to process
  }

  // For each edge:
  // 1. See if there is a perpendicular from hit point to the edge.
  // 2. If yes, add it as a dimension axis.
  for (let i = 0; i < lineVertices.length; i += 2) {
    let minDistanceToEdge1 = Number.MAX_VALUE;
    let minDistanceToEdge2 = Number.MAX_VALUE;
    const vA = lineVertices[i];
    const vB = lineVertices[i + 1];

    const edgeDir = new THREE.Vector3().subVectors(vB, vA).normalize();

    // define vector that is perpendicular to the edge and the surface normal
    // Use it to define a plane that the edge lies on, which is orthogonal to the hit surface
    const edgePerpendicular = new THREE.Vector3().crossVectors(faceNormal, edgeDir).normalize().negate();
    let skip = false;
    for (const axis of axes) {
      if (new THREE.Vector3().crossVectors(axis, edgePerpendicular).length() < EPSILON) {
        skip = true;
        break;
      }
    }
    if (skip) {
      // We've processed this direction already
      continue;
    }

    let distance;

    const ray = new THREE.Ray(hitPoint, edgePerpendicular);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(edgePerpendicular, vA);
    let found = ray.intersectPlane(plane, new THREE.Vector3());
    if (found) {
      distance = found.distanceTo(hitPoint);
      if (distance < minDistanceToEdge1) {
        const testPt = new THREE.Vector3().addVectors(hitPoint, edgePerpendicular.clone().multiplyScalar(distance));
        if (isInLineSegment(testPt, vA, vB)) {
          axes.push(edgePerpendicular);
        }
      } else {
        distance = -1.0;
        const oppositeRay = new THREE.Ray(hitPoint, edgePerpendicular.clone().negate());
        found = oppositeRay.intersectPlane(plane, new THREE.Vector3());
        if (found && distance < minDistanceToEdge2) {
          distance = found.distanceTo(hitPoint);
          const testPt = new THREE.Vector3().subVectors(hitPoint, edgePerpendicular.clone().multiplyScalar(distance));
          if (isInLineSegment(testPt, vA, vB)) {
            axes.push(edgePerpendicular.clone().negate());
          }
        }
      }
    }
  }

  // There is a special case where a circle is represented by a high-order polygon. If the user hits the center
  // of the circle, we get a bunch of radial axes that all show the same measurement. In that case, limit the axes
  // to two, picking the first, and one that is orthogonal to the first if any. If not, then pick the second.
  // Good test case is the top of the revolving door in Spec Office which is a 32-sided polygon.
  if (axes.length > 1 && lineVertices.length > MAX_POLYGON_AXES * 10) {
    let index1 = 0;
    for (let i = 1; i < axes.length; i++) {
      if (Math.abs(axes[0].dot(axes[i])) < EPSILON) {
        index1 = i;
        break;
      }
    }

    if (index1 !== 0) {
      axes[1] = axes[index1];
      axes.splice(2);
    } else if (axes.length > 0) {
      axes.splice(1);
    }
  }

  // Compute the dimensions. The axes that were collected processing individual edges
  // Now have to be processed to see if they produce a dimension by intersecting two edges.
  axes.forEach((axis) => {
    let minDistanceToEdge1 = Number.MAX_VALUE;
    let minDistanceToEdge2 = Number.MAX_VALUE;
    let points = {};

    for (let i = 0; i < lineVertices.length; i += 2) {
      const vA = lineVertices[i];
      const vB = lineVertices[i + 1];

      const edgeDir = new THREE.Vector3().subVectors(vB, vA).normalize();
      const edgePerpendicular = new THREE.Vector3().crossVectors(faceNormal, edgeDir).normalize();

      // The axis is not parallel to the edge, so skip it
      if (Math.abs(axis.dot(edgeDir)) > EPSILON) {
        continue;
      }

      let distance1 = -1.0;
      const ray1 = new THREE.Ray(hitPoint, axis);
      const plane1 = new THREE.Plane().setFromNormalAndCoplanarPoint(edgePerpendicular, vA);
      let found1 = ray1.intersectPlane(plane1, new THREE.Vector3());
      if (found1) {
        distance1 = found1.distanceTo(hitPoint);
        if (distance1 < minDistanceToEdge1) {
          const testPt = new THREE.Vector3().addVectors(hitPoint, axis.clone().multiplyScalar(distance1));
          if (isInLineSegment(testPt, vA, vB)) {
            points.p1 = found1;
            minDistanceToEdge1 = distance1;
          }
        }
      }

      let distance2 = -1.0;
      const oppositeAxisRay = new THREE.Ray(hitPoint, axis.clone().negate());
      let found2 = oppositeAxisRay.intersectPlane(plane1, new THREE.Vector3());
      if (found2) {
        distance2 = found2.distanceTo(hitPoint);
        if (distance2 < minDistanceToEdge2) {
          const testPt = new THREE.Vector3().subVectors(hitPoint, axis.clone().multiplyScalar(distance2));
          if (isInLineSegment(testPt, vA, vB)) {
            points.p2 = found2;
            minDistanceToEdge2 = distance2;
          }
        }
      }
    }

    if (minDistanceToEdge1 < Number.MAX_VALUE && minDistanceToEdge2 < Number.MAX_VALUE) {
      if (points.p1.distanceTo(points.p2) > POINT_PRECISION) {
        dimensionPoints = [
        ...(dimensionPoints || []),
        points];

      }
    }
  });

  return dimensionPoints;
}

/**
 * Gets the intersection point between a ray starting at the picked point and extending along the face normal
 * @param {Autodesk.Viewing.Viewer3D} viewer - The viewer instance
 * @param {Object} firstPick - The pick data containing intersection point and face information
 * @param {THREE.Vector3} normal - The face normal vector (NOTE: this vector may be modified/negated by this function)
 * @returns {Object|null} The intersection point data if found, null otherwise
 */
function getLaserIntersection(viewer, firstPick, normal) {
  const camera = viewer.getCamera();
  const isOrthographic = camera.isOrthographicCamera;
  const faceNormal = firstPick.faceNormal;

  // Get viewing direction: from camera to intersection point (perspective) or camera to target (orthographic)
  const viewingDirection = isOrthographic ?
  new THREE.Vector3().subVectors(camera.position, camera.target).normalize() :
  new THREE.Vector3().subVectors(camera.position, firstPick.intersectPoint).normalize();

  // Flip normal if facing away from camera (address VIZX-2919)
  if (viewingDirection.dot(faceNormal) < 0) {
    normal.negate();
  }

  const ray = new THREE.Ray(firstPick.intersectPoint || firstPick.intersection, normal);
  const fullResult = viewer.impl.rayIntersect(ray, false, undefined, undefined, undefined);

  if (!fullResult) return null;

  // Extract only essential data
  return {
    intersectPoint: fullResult.point || fullResult.intersectPoint,
    distance: fullResult.distance,
    fragId: fullResult.fragId,
    dbId: fullResult.dbId,
    modelId: fullResult.modelId
  };
}

/**
 * Computes the laser measurement result from picks
 * @param {Object} picks - The measurement picks
 * @param {Object} viewer - The viewer instance
 * @returns {Object|null} The laser measurement result
 */
export function computeLaserResult(picks, viewer) {
  const firstPick = picks[1];
  const faceNormal = picks[1] && picks[1].faceNormal;
  if (!firstPick || !faceNormal)
  return null;

  // we measure dimensions for parallel edges only
  // either use the existing dimensions or calculate them, depends if the measurement was restored or not
  const dimensions = firstPick.dimensions ?? getDimensionPoints(firstPick);
  // Create a copy of the normal to avoid modifying the original pick data
  // need to reconstruct the normal because sometimes we get recontstucted data from `createMeasurementFromData` instead of direct computation
  const normal = new THREE.Vector3(faceNormal.x, faceNormal.y, faceNormal.z);
  const laserHitPoint = getLaserIntersection(viewer, firstPick, normal);

  if (!dimensions && !laserHitPoint) {
    return null;
  }

  // 4 possible outcomes for the measurement:
  // 1. Laser point, but no dimensions
  // 2. Dimensions but no laser point
  // 3. Both laser intersection point and dimensions
  // 4. None
  const measures = {
    intersectPoint: firstPick.intersectPoint,
    laserHitPoint,
    dimensions: dimensions,
    type: MeasureCommon.MeasurementTypes.MEASUREMENT_LASER
  };

  return measures;
}